import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { conjugatePresentFormII_Salim_fromRow } from './utils/arPresentFormII_salim';
import { conjugatePresentFormIII_Salim_fromRow } from './utils/arPresentFormIII_salim';
import { conjugatePresentFormIV_Salim_fromRow } from './utils/arPresentFormIV_salim';
import { conjugatePresentFormV_Salim_fromRow } from './utils/arPresentFormV_salim';
import { buildImperativeFromRowII } from './utils/imperativeFormII_salim';
import { buildImperativeFromRowIII } from './utils/imperativeFormIII_salim';
import { buildImperativeFromRowIV } from './utils/imperativeFormIV_salim';
import html2canvas from 'html2canvas';
import alarmClockIcon from './imgs/alarm-clock.svg';
import './VerbTenseGame.css';

const FORMS = [
  { id: 'עבר', name: 'עבר' },
  { id: 'הווה-עתיד', name: 'הווה-עתיד' },
  { id: 'ציווי', name: 'ציווי' },
  { id: 'בינוני פועל', name: 'בינוני פועל' },
  { id: 'בינוני פעול', name: 'בינוני פעול' },
  { id: 'המקור', name: 'המקור' }
];

const PRONOUNS = [
  { arabic: 'أَنَا' },
  { arabic: 'أَنْتَ' },
  { arabic: 'أَنْتِ' },
  { arabic: 'هُوَ' },
  { arabic: 'هِيَ' },
  { arabic: 'نَحْنُ' },
  { arabic: 'أَنْتُمْ' },
  { arabic: 'أَنْتُنَّ' },
  { arabic: 'هُمْ' },
  { arabic: 'هُنَّ' }
];

const IMPERATIVE_KEYS = ['you_ms', 'you_fs', 'you_mp', 'you_fp'];
const IMPERATIVE_KEY_TO_ARABIC = {
  'you_ms': 'أَنْتَ',
  'you_fs': 'أَنْتِ',
  'you_mp': 'أَنْتُمْ',
  'you_fp': 'أَنْتُنَّ'
};

function VerbTenseGame({ gameConfig }) {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  const [gameVerbs, setGameVerbs] = useState([]);
  const [tenseOrder, setTenseOrder] = useState([]);
  const [imperativeKeySequence, setImperativeKeySequence] = useState([]);
  const [currentVerbIndex, setCurrentVerbIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [draggedKey, setDraggedKey] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [draggedElement, setDraggedElement] = useState(null);
  const [hoveredLock, setHoveredLock] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [currentAttempts, setCurrentAttempts] = useState(0);
  const [hasError, setHasError] = useState(false);
  const resultsSummaryRef = useRef(null);
  
  // Create a single AudioContext for all sounds (mobile optimization)
  const audioContextRef = useRef(null);
  
  // Initialize AudioContext on first user interaction
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume context if suspended (required for iOS/Android)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };
  
  useEffect(() => {
    // Clean up on unmount
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  // יצירת שאלה חדשה
  const generateQuestion = useCallback((verbs, tenses, index, imperativeKeys) => {
    if (index >= verbs.length) {
      setShowResults(true);
      return;
    }
    
    const verbData = verbs[index];
    const targetTense = tenses[index];
    
    let conjugations;
    if (targetTense === 'עבר') {
      conjugations = conjugatePastGeneric_Salim(verbData['פועל']);
    } else if (targetTense === 'הווה-עתיד') {
      conjugations = conjugatePresentFormV_Salim_fromRow(verbData) || conjugatePresentFormIV_Salim_fromRow(verbData) || conjugatePresentFormIII_Salim_fromRow(verbData) || conjugatePresentFormII_Salim_fromRow(verbData) || conjugatePresentFormI_Salim_fromRow(verbData);
    }
    
    if (targetTense === 'ציווי') {
      const targetBinyan = (gameConfig && gameConfig.binyan) || '1';
      const imperativeForms = targetBinyan === '4'
        ? buildImperativeFromRowIV(verbData)
        : targetBinyan === '3'
        ? buildImperativeFromRowIII(verbData)
        : buildImperativeFromRowII(verbData);
      if (!imperativeForms) return;
      const randomKey = (imperativeKeys && imperativeKeys[index]) || IMPERATIVE_KEYS[Math.floor(Math.random() * IMPERATIVE_KEYS.length)];
      const imperativeVerb = imperativeForms[randomKey];
      if (!imperativeVerb) return;
      setCurrentQuestion({
        verb: imperativeVerb,
        verbData: verbData,
        correctForm: 'ציווי',
        pronoun: { arabic: IMPERATIVE_KEY_TO_ARABIC[randomKey] }
      });
      setFeedback(null);
      setSelectedLock(null);
      setDraggedKey(false);
      setIsDragging(false);
      setDraggedElement(null);
      setHoveredLock(null);
      setCurrentAttempts(0);
      setHasError(false);
      return;
    }

    if (!conjugations) return;
    
    // בחירת גוף אקראי
    const availablePronouns = PRONOUNS.filter(p => conjugations[p.arabic]);
    const randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
    const conjugatedVerb = conjugations[randomPronoun.arabic];
    
    setCurrentQuestion({
      verb: conjugatedVerb,
      verbData: verbData,
      correctForm: targetTense,
      pronoun: randomPronoun
    });
    
    setFeedback(null);
    setSelectedLock(null);
    setDraggedKey(false);
    setIsDragging(false);
    setDraggedElement(null);
    setHoveredLock(null);
    setCurrentAttempts(0);
    setHasError(false);
  }, []);

  // פונקציה לערבוב מאוזן - מונעת יותר מ-2 רצפים של אותו זמן
  const createBalancedTenseSequence = () => {
    // ניסיון לבנות רצף תקין - אם נכשל, ננסה שוב
    let attempts = 0;
    const maxAttempts = 100;
    
    while (attempts < maxAttempts) {
      attempts++;
      const result = [];
      let pastRemaining = 5;
      let futureRemaining = 5;
      let lastTense = null;
      let consecutiveCount = 0;
      let failed = false;
      
      while ((pastRemaining > 0 || futureRemaining > 0) && !failed) {
        let canChoosePast = pastRemaining > 0;
        let canChooseFuture = futureRemaining > 0;
        
        // בדיקה: האם בחירה תיצור 3 רצופים?
        if (canChoosePast && lastTense === 'עבר' && consecutiveCount >= 2) {
          canChoosePast = false; // לא ניתן לבחור עבר
        }
        if (canChooseFuture && lastTense === 'הווה-עתיד' && consecutiveCount >= 2) {
          canChooseFuture = false; // לא ניתן לבחור עתיד
        }
        
        // אם אין אפשרות - ניסיון נכשל, ננסה שוב
        if (!canChoosePast && !canChooseFuture) {
          failed = true;
          break;
        }
        
        let choosePast;
        if (!canChoosePast) {
          choosePast = false;
        } else if (!canChooseFuture) {
          choosePast = true;
        } else {
          // שניהם אפשריים - בחר אקראית עם העדפה למה שנשאר יותר
          const pastWeight = pastRemaining / (pastRemaining + futureRemaining);
          choosePast = Math.random() < pastWeight;
        }
        
        if (choosePast) {
          result.push('עבר');
          pastRemaining--;
          if (lastTense === 'עבר') {
            consecutiveCount++;
          } else {
            consecutiveCount = 1;
            lastTense = 'עבר';
          }
        } else {
          result.push('הווה-עתיד');
          futureRemaining--;
          if (lastTense === 'הווה-עתיד') {
            consecutiveCount++;
          } else {
            consecutiveCount = 1;
            lastTense = 'הווה-עתיד';
          }
        }
      }
      
      // אם הצלחנו לבנות רצף שלם - החזר אותו
      if (!failed && result.length === 10) {
        return result;
      }
    }
    
    // במקרה הגרוע ביותר (לא אמור לקרות) - החזר רצף לסירוגין
    return ['עבר', 'הווה-עתיד', 'עבר', 'הווה-עתיד', 'עבר', 'הווה-עתיד', 'עבר', 'הווה-עתיד', 'עבר', 'הווה-עתיד'];
  };

  // יצירת רצף של שלושת זמנים: עבר + הווה-עתיד + ציווי
  const createAllThreeTensesSequence = () => {
    const allTenses = ['עבר', 'הווה-עתיד', 'ציווי'];
    const randomTense = allTenses[Math.floor(Math.random() * allTenses.length)];
    const source = ['עבר', 'עבר', 'עבר', 'הווה-עתיד', 'הווה-עתיד', 'הווה-עתיד', 'ציווי', 'ציווי', 'ציווי', randomTense];
    const hasThreeConsecutiveForms = (sequence) =>
      sequence.some((tense, index) => index >= 2 && tense === sequence[index - 1] && tense === sequence[index - 2]);

    let base = null;
    for (let attempt = 0; attempt < 100; attempt++) {
      const candidate = [...source];
      // Fisher-Yates shuffle
      for (let i = candidate.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [candidate[i], candidate[j]] = [candidate[j], candidate[i]];
      }

      if (!hasThreeConsecutiveForms(candidate)) {
        base = candidate;
        break;
      }
    }

    if (!base) {
      base = ['עבר', 'הווה-עתיד', 'ציווי', 'עבר', 'הווה-עתיד', 'ציווי', 'עבר', 'הווה-עתיד', 'ציווי', randomTense];
    }

    // הכנת גופי ציווי מראש — מעורבבים, ללא חזרה
    const shuffledImperativeKeys = [...IMPERATIVE_KEYS];
    for (let i = shuffledImperativeKeys.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledImperativeKeys[i], shuffledImperativeKeys[j]] = [shuffledImperativeKeys[j], shuffledImperativeKeys[i]];
    }
    let imperativeIdx = 0;
    const imperativeKeySeq = base.map(tense => {
      if (tense === 'ציווי') {
        return shuffledImperativeKeys[imperativeIdx++ % shuffledImperativeKeys.length];
      }
      return null;
    });
    return { tenseSequence: base, imperativeKeySeq };
  };

  // טעינת פעלים
  useEffect(() => {
    const loadVerbs = async () => {
      try {
        const rows = await loadVerbsCsv('/verbs-grade10.csv');
        
        const targetBinyan = (gameConfig && gameConfig.binyan) || '1';
        const filteredVerbs = rows.filter(r => {
          const binyan = (r['בניין'] || '').trim();
          const gizra = (r['gizra'] || '').trim();
          const tense = r['זמן'];
          
          const gizraOk = targetBinyan === '5'
            ? ['שלמים', 'כפולים', 'ע"ע', 'פ"ו', 'פ"י', 'ע"ו', 'ע"י'].some(pattern => gizra.includes(pattern))
            : targetBinyan === '2'
            ? (gizra.includes('שלמים') || gizra.includes('כפולים') || gizra.includes('ע"ו') || gizra.includes('פ"ו'))
            : targetBinyan === '3'
            ? (gizra.includes('שלמים') || gizra.includes('ע"ו') || gizra.includes('פ"ו'))
            : gizra.includes('שלמים');
          return (binyan === targetBinyan || binyan === 'בניין ' + targetBinyan) &&
                 gizraOk &&
                 tense === 'עבר';
        });
        
        // Fisher-Yates shuffle לפעלים
        const shuffled = [...filteredVerbs];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const selected = shuffled.slice(0, 10);
        
        // יצירת סדר זמנים מאוזן
        let tenseSequence, imperativeKeySeq = null;
        if (gameConfig && gameConfig.withImperative) {
          const result = createAllThreeTensesSequence();
          tenseSequence = result.tenseSequence;
          imperativeKeySeq = result.imperativeKeySeq;
        } else {
          tenseSequence = createBalancedTenseSequence();
        }
        
        setGameVerbs(selected);
        setTenseOrder(tenseSequence);
        setImperativeKeySequence(imperativeKeySeq || []);
        generateQuestion(selected, tenseSequence, 0, imperativeKeySeq);
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
      }
    };
    
    loadVerbs();
  }, [generateQuestion]);

  // Pointer drag handlers (works for both mouse and touch)
  const handlePointerStart = (e) => {
    if (feedback === 'correct') return;
    
    e.preventDefault();
    setDraggedKey(true);
    setIsDragging(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDraggedElement(e.currentTarget);
    
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.style.zIndex = '1000';
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !draggedElement) return;
    
    const dx = e.clientX - dragStartPos.x;
    const dy = e.clientY - dragStartPos.y;
    
    draggedElement.style.transform = `translate(${dx}px, ${dy}px)`;
    
    // בדיקה מעל איזה מנעול המפתח נמצא
    const locks = document.querySelectorAll('.lock');
    let foundHoveredLock = null;
    
    locks.forEach((lock) => {
      const rect = lock.getBoundingClientRect();
      const margin = 30;
      
      if (e.clientX >= rect.left - margin &&
          e.clientX <= rect.right + margin &&
          e.clientY >= rect.top - margin &&
          e.clientY <= rect.bottom + margin) {
        
        const formId = lock.getAttribute('data-form-id');
        if (formId) {
          foundHoveredLock = formId;
        }
      }
    });
    
    setHoveredLock(foundHoveredLock);
  };

  const handlePointerEnd = (e) => {
    if (!isDragging || !draggedElement) return;
    
    draggedElement.style.transform = '';
    draggedElement.style.zIndex = '';
    
    // השתמש רק במנעול שהיה עם אנימציית הגדילה (hoveredLock)
    if (hoveredLock) {
      setSelectedLock(hoveredLock);
      checkAnswer(hoveredLock);
    }
    
    setIsDragging(false);
    setDraggedKey(false);
    setDraggedElement(null);
    setHoveredLock(null);
  };

  // בדיקת תשובה
  const checkAnswer = (formId) => {
    const isCorrect = formId === currentQuestion.correctForm;
    
    // ספירת ניסיון - חשב את המספר המעודכן
    const updatedAttempts = currentAttempts + 1;
    setCurrentAttempts(updatedAttempts);
    
    setFeedback(isCorrect ? 'correct' : 'wrong');
    
    if (isCorrect) {
      playSuccessSound();
      setTimeout(() => {
        saveToHistory(updatedAttempts);
        nextQuestion();
      }, 600);
    } else {
      // סימון שהיה שגיאה במילה זו
      setHasError(true);
      playErrorSound();
      // מאפשר לנסות שוב - מנקה את הבחירה אחרי זמן קצר
      setTimeout(() => {
        setFeedback(null);
        setSelectedLock(null);
      }, 800);
    }
  };

  // שמירת תשובה בהיסטוריה
  const saveToHistory = (finalAttempts) => {
    setGameHistory(prev => [...prev, {
      verb: currentQuestion.verb,
      verbData: currentQuestion.verbData,
      correctForm: currentQuestion.correctForm,
      userAnswer: selectedLock,
      attempts: finalAttempts,
      hadError: hasError
    }]);
  };

  // מעבר לשאלה הבאה
  const nextQuestion = () => {
    const nextIndex = currentVerbIndex + 1;
    setCurrentVerbIndex(nextIndex);
    generateQuestion(gameVerbs, tenseOrder, nextIndex, imperativeKeySequence);
  };

  // צלילים
  const playSuccessSound = () => {
    try {
      initAudioContext();
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 523.25;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const playErrorSound = () => {
    try {
      initAudioContext();
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  // חזרה לבחירת משחקים
  const handleBackToGames = () => {
    navigate(getBackUrl());
  };

  // שיתוף תוצאות - פונקציות עזר
  const generateResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;
    const canvas = await html2canvas(resultsSummaryRef.current, {
      backgroundColor: '#5eb9c5',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = 'תוצאות-מהי-הצורה-הדקדוקית.png';
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'תוצאות מהי הצורה הדקדוקית?',
          text: `מהי הצורה הדקדוקית?\nהשגתי ${gameHistory.filter(h => !h.hadError).length}/${gameHistory.length}!`,
          files: [file],
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('שגיאה בשיתוף:', err);
        }
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = URL.createObjectURL(blob);
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = 'תוצאות-מהי-הצורה-הדקדוקית.png';
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  if (showResults) {
    const correctCount = gameHistory.filter(h => !h.hadError).length;

    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>מהי הצורה הדקדוקית?</h3>
            <div className="final-score-display">
              ניקוד: {correctCount}/{gameHistory.length}
            </div>
            <br/>
            <div className="words-list">
              {gameHistory.map((item, idx) => (
                <div key={idx} className="word-item">
                  <div className="word-pair">
                    <span className="arabic-word-summary">{item.verb}</span>
                    <span className="separator"> - </span>
                    <span className="hebrew-word">{item.correctForm}</span>
                  </div>
                  <div className={`attempts-info ${item.hadError ? 'multiple-attempts' : ''}`}>
                    {!item.hadError ? (
                      <span>✓ תשובה נכונה</span>
                    ) : (
                      <span>✗ נסיונות: {item.attempts}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק מחדש
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="grammatical-form-game loading">טוען משחק...</div>;
  }

  const withImperative = gameConfig && gameConfig.withImperative;
  const displayForms = [FORMS[0], FORMS[1]];

  const keyCard = (
    <div 
      className={`key-container ${draggedKey ? 'dragging' : ''} ${feedback === 'correct' ? 'correct-pulse' : ''}`}
      onPointerDown={handlePointerStart}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      style={{ touchAction: 'none' }}
    >
      <div className="key">
        <div className="key-icon">👈🏻</div>
        <div className="key-verb">{currentQuestion.verb}</div>
      </div>
      <div className="progress">{currentVerbIndex + 1}/{gameVerbs.length}</div>
    </div>
  );

  const makeLock = (form, index, extraClass) => (
    <div
      key={form.id}
      data-form-id={form.id}
      data-lock-name={form.name}
      className={`lock ${extraClass || ('lock-position-' + index)} ${
        selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
      } ${hoveredLock === form.id ? 'hovered' : ''}`}
    >
      <span className="lock-icon" aria-hidden="true">
        <img className="lock-icon-image" src={alarmClockIcon} alt="" />
      </span>
      <div className="lock-label">{form.name}</div>
    </div>
  );

  if (withImperative) {
    return (
      <div className="verb-tense-game mobile-three-lock-layout">
        <div className="game-header">
          <button onClick={handleBackToGames} className="back-to-verbs-button">
            ↩ חזרה למשחקי הפועל
          </button>
          <h1>מהי הצורה הדקדוקית?</h1>
          <p className="game-instructions-animate">גרור את היד לשעון הנכון</p>
        </div>
        <div className="tense-game-board">
          <div className="locks-row locks-row-top">
            {displayForms.map((form, index) => makeLock(form, index))}
          </div>
          {keyCard}
          <div className="locks-row locks-row-bottom">
            {makeLock(FORMS[2], 0, 'lock-bottom')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grammatical-form-game mobile-two-lock-layout">
      <div className="game-header">
        <button onClick={handleBackToGames} className="back-to-verbs-button">
          ↩ חזרה למשחקי הפועל
        </button>
        <h1>מהי הצורה הדקדוקית?</h1>
        <p className="game-instructions-animate">גרור את היד לשעון הנכון</p>
      </div>
      <div className="grammatical-game-board">
        <div className="locks-row top-locks">
          {displayForms.map((form, index) => makeLock(form, index))}
        </div>
        {keyCard}
      </div>
    </div>
  );
}

export default VerbTenseGame;
